<script setup lang="ts">
  import { ref, watch } from 'vue'
  import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
  import { Button } from '@/components/ui/button'
  import {
    DeliveryEventsService,
    type CustomerEmail,
    type CustomerDeliveryEvent
  } from '@/types/generated'
  import { useTranslation } from '@/composables/useTranslation'
  import {
    Loader2,
    CheckIcon,
    XCircleIcon,
    EyeIcon,
    MousePointerClickIcon,
    MailIcon,
    AlertTriangleIcon,
    ShieldXIcon
  } from 'lucide-vue-next'

  const props = defineProps<{
    open: boolean
    email: CustomerEmail | null
  }>()

  const emit = defineEmits<{
    'update:open': [value: boolean]
  }>()

  const { t } = useTranslation({
    en: {
      deliveryStatusDetails: 'Delivery Status Details',
      loading: 'Loading delivery events...',
      noEvents: 'No delivery events found for this email.',
      error: 'Failed to load delivery events.',
      close: 'Close',
      eventTime: 'Time',
      eventType: 'Event',
      eventDescription: 'Description',
      deliveryEventSent: 'Sent',
      deliveryEventDelivered: 'Delivered',
      deliveryEventOpened: 'Opened',
      deliveryEventClicked: 'Clicked',
      deliveryEventHardBounced: 'Not Deliverable',
      deliveryEventSoftBounced: 'Not Deliverable',
      deliveryEventBlocked: 'Not Deliverable',
      deliveryEventSpam: 'Spam',
      deliveryEventUnsubscribed: 'Unsubscribed',
      deliveryEventUnknown: 'Unknown'
    },
    de: {
      deliveryStatusDetails: 'Zustellstatus Details',
      loading: 'Lade Zustellereignisse...',
      noEvents: 'Keine Zustellereignisse für diese E-Mail gefunden.',
      error: 'Fehler beim Laden der Zustellereignisse.',
      close: 'Schließen',
      eventTime: 'Zeit',
      eventType: 'Ereignis',
      eventDescription: 'Beschreibung',
      deliveryEventSent: 'Gesendet',
      deliveryEventDelivered: 'Zugestellt',
      deliveryEventOpened: 'Geöffnet',
      deliveryEventClicked: 'Angeklickt',
      deliveryEventHardBounced: 'Nicht zustellbar',
      deliveryEventSoftBounced: 'Nicht zustellbar',
      deliveryEventBlocked: 'Nicht zustellbar',
      deliveryEventSpam: 'Als Spam markiert',
      deliveryEventUnsubscribed: 'Abgemeldet',
      deliveryEventUnknown: 'Unbekannt'
    }
  })

  const isLoading = ref(false)
  const hasError = ref(false)
  const deliveryEvents = ref<CustomerDeliveryEvent[]>([])

  // Watch for changes in the email prop and load delivery events
  watch(
    () => props.email,
    async (newEmail) => {
      if (!newEmail || !props.open) {
        return
      }

      await loadDeliveryEvents(newEmail.uuid)
    },
    { immediate: true }
  )

  // Watch for modal opening
  watch(
    () => props.open,
    async (isOpen) => {
      if (isOpen && props.email) {
        await loadDeliveryEvents(props.email.uuid)
      }
    }
  )

  const loadDeliveryEvents = async (emailUuid: string) => {
    isLoading.value = true
    hasError.value = false
    deliveryEvents.value = []

    try {
      const response = await DeliveryEventsService.deliveryEventsList({
        email: emailUuid
      })
      const results = response.results as CustomerDeliveryEvent[]

      // Sort ascending by created_at so oldest is on top
      const sortedEvents = [...results].sort((a, b) => {
        return new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
      })

      // Check if there's a failure event in the timeline
      const failureEvents = ['HARD_BOUNCED', 'SOFT_BOUNCED', 'BLOCKED', 'SPAM']
      const failureEventTimestamps = sortedEvents
        .filter((event) => failureEvents.includes(event.event))
        .map((event) => new Date(event.created_at).getTime())

      // Threshold: hide success events occurring immediately after a failure (1 minute)
      const THRESHOLD_MS = 60 * 1000

      // If there's a failure event, hide success events that happened before or within the threshold of any failure
      if (failureEventTimestamps.length > 0) {
        const earliestFailureTime = Math.min(...failureEventTimestamps)
        const successEventsToHide = ['DELIVERED', 'OPENED', 'CLICKED']

        deliveryEvents.value = sortedEvents.filter((event) => {
          if (!successEventsToHide.includes(event.event)) {
            return true
          }

          const eventTime = new Date(event.created_at).getTime()

          if (eventTime <= earliestFailureTime) {
            return false
          }

          const isWithinThreshold = failureEventTimestamps.some((failureTime) => {
            return eventTime >= failureTime && eventTime - failureTime <= THRESHOLD_MS
          })

          return !isWithinThreshold
        })
      } else {
        deliveryEvents.value = sortedEvents
      }
    } catch (error) {
      console.error('Error loading delivery events:', error)
      hasError.value = true
    } finally {
      isLoading.value = false
    }
  }

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    })
  }

  const getEventIcon = (event: string) => {
    switch (event) {
      case 'SENT':
        return MailIcon
      case 'DELIVERED':
        return CheckIcon
      case 'OPENED':
        return EyeIcon
      case 'CLICKED':
        return MousePointerClickIcon
      case 'HARD_BOUNCED':
      case 'SOFT_BOUNCED':
        return XCircleIcon
      case 'BLOCKED':
        return ShieldXIcon
      case 'SPAM':
        return AlertTriangleIcon
      case 'UNSUBSCRIBED':
        return XCircleIcon
      default:
        return MailIcon
    }
  }

  const getEventStyle = (event: string) => {
    switch (event) {
      case 'SENT':
        return 'bg-blue-100 text-blue-800'
      case 'DELIVERED':
        return 'bg-green-100 text-green-800'
      case 'OPENED':
        return 'bg-purple-100 text-purple-800'
      case 'CLICKED':
        return 'bg-indigo-100 text-indigo-800'
      case 'HARD_BOUNCED':
      case 'SOFT_BOUNCED':
        return 'bg-red-100 text-red-800'
      case 'BLOCKED':
      case 'SPAM':
        return 'bg-orange-100 text-orange-800'
      case 'UNSUBSCRIBED':
        return 'bg-gray-100 text-gray-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getEventLabel = (event: string) => {
    switch (event) {
      case 'SENT':
        return t('deliveryEventSent')
      case 'DELIVERED':
        return t('deliveryEventDelivered')
      case 'OPENED':
        return t('deliveryEventOpened')
      case 'CLICKED':
        return t('deliveryEventClicked')
      case 'HARD_BOUNCED':
        return t('deliveryEventHardBounced')
      case 'SOFT_BOUNCED':
        return t('deliveryEventSoftBounced')
      case 'BLOCKED':
        return t('deliveryEventBlocked')
      case 'SPAM':
        return t('deliveryEventSpam')
      case 'UNSUBSCRIBED':
        return t('deliveryEventUnsubscribed')
      default:
        return t('deliveryEventUnknown')
    }
  }

  const shouldShowDescription = (event: string) => {
    return event === 'HARD_BOUNCED' || event === 'SOFT_BOUNCED' || event === 'BLOCKED'
  }
</script>

<template>
  <Dialog
    :open="open"
    @update:open="emit('update:open', $event)"
  >
    <DialogContent class="max-h-[80vh] sm:max-w-[700px]">
      <DialogHeader>
        <DialogTitle>{{ t('deliveryStatusDetails') }}</DialogTitle>
      </DialogHeader>

      <div class="py-4">
        <!-- Loading State -->
        <div
          v-if="isLoading"
          class="flex items-center justify-center py-8"
        >
          <div class="flex items-center gap-2">
            <Loader2 class="h-4 w-4 animate-spin" />
            <span class="text-sm text-gray-600">{{ t('loading') }}</span>
          </div>
        </div>

        <!-- Error State -->
        <div
          v-else-if="hasError"
          class="py-8 text-center"
        >
          <p class="text-sm text-red-600">{{ t('error') }}</p>
        </div>

        <!-- No Events State -->
        <div
          v-else-if="deliveryEvents.length === 0"
          class="py-8 text-center"
        >
          <p class="text-sm text-gray-500">{{ t('noEvents') }}</p>
        </div>

        <!-- Events List -->
        <div
          v-else
          class="space-y-3"
        >
          <div
            v-for="event in deliveryEvents"
            :key="event.uuid"
            class="flex items-start gap-3 rounded-md bg-gray-50 p-3"
          >
            <div class="flex h-8 w-8 items-center justify-center rounded-full border bg-white">
              <component
                :is="getEventIcon(event.event)"
                class="h-4 w-4 text-gray-600"
              />
            </div>

            <div class="min-w-0 flex-1">
              <div class="mb-1 flex items-center gap-2">
                <span
                  class="inline-flex items-center rounded-full px-2 py-1 text-xs font-medium"
                  :class="getEventStyle(event.event)"
                >
                  {{ getEventLabel(event.event) }}
                </span>
                <span class="text-xs text-gray-500">
                  {{ formatDateTime(event.created_at) }}
                </span>
              </div>

              <div
                v-if="event.event_description && shouldShowDescription(event.event)"
                class="mt-2 rounded-md border border-gray-200 bg-white p-3"
              >
                <div class="mb-1 text-xs font-medium uppercase tracking-wide text-gray-500">
                  {{ t('eventDescription') }}
                </div>
                <div class="whitespace-pre-line break-words text-sm leading-relaxed text-gray-700">
                  {{ event.event_description }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="flex justify-end">
        <Button
          variant="secondary"
          @click="emit('update:open', false)"
        >
          {{ t('close') }}
        </Button>
      </div>
    </DialogContent>
  </Dialog>
</template>
